import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "mall-wish__shell" }
const _hoisted_2 = { class: "mall-wish__head" }
const _hoisted_3 = {
  key: 0,
  class: "mall-wish__bulk no-print"
}
const _hoisted_4 = { class: "mall-wish__check-all" }
const _hoisted_5 = ["checked", ".indeterminate"]
const _hoisted_6 = ["disabled"]
const _hoisted_7 = ["disabled"]
const _hoisted_8 = {
  key: 0,
  class: "mall-wish__alert"
}
const _hoisted_9 = {
  key: 1,
  class: "mall-wish__empty"
}
const _hoisted_10 = {
  key: 2,
  class: "mall-wish__grid"
}
const _hoisted_11 = { class: "mall-wish__select" }
const _hoisted_12 = ["checked", "aria-label", "onChange"]
const _hoisted_13 = {
  key: 1,
  class: "mall-wish__missing"
}
const _hoisted_14 = ["onClick"]
const _hoisted_15 = {
  key: 2,
  class: "mall-wish__badge"
}
const _hoisted_16 = {
  key: 0,
  class: "mall-wish__undo",
  role: "status"
}

import { computed, onMounted, ref, watch } from 'vue'
import ProductCard from '@/components/mall/ProductCard.vue'
import AppIcon from '@/components/common/AppIcon.vue'
import { ensureLiveProducts } from '@/api/mall'
import { useCart } from '@/composables/useCart'
import { useMallMypageEmbedded } from '@/composables/useMallMypage'
import { useMallToast } from '@/composables/useMallToast'
import { useWishlist } from '@/composables/useWishlist'
import { getProductById } from '@/constants/mallProducts'


export default /*@__PURE__*/_defineComponent({
  __name: 'MallWishlistPage',
  setup(__props) {

const isEmbedded = useMallMypageEmbedded()
const { wishlist, removeWish, removeMany, addMany } = useWishlist()
const { addItem } = useCart()
const { showSuccess, showError } = useMallToast()

const selectedIds = ref<string[]>([])
const adding = ref(false)
const undoMessage = ref('')
let undoTimer: ReturnType<typeof setTimeout> | undefined
let lastRemoved: string[] = []

const rows = computed(() =>
  wishlist.value.map((id) => {
    const product = getProductById(id) || null
    return {
      id,
      product,
      issue: !product || Boolean(product.soldOut),
    }
  }),
)

const selectableIds = computed(() => rows.value.filter((row) => !row.issue).map((row) => row.id))
const issueCount = computed(() => rows.value.filter((row) => row.issue).length)
const allSelectableChecked = computed(
  () =>
    rows.value.length > 0 && rows.value.every((row) => selectedIds.value.includes(row.id)),
)
const partialSelected = computed(
  () => selectedIds.value.length > 0 && !allSelectableChecked.value,
)

function toggleSelect(id: string) {
  if (selectedIds.value.includes(id)) {
    selectedIds.value = selectedIds.value.filter((item) => item !== id)
  } else {
    selectedIds.value = [...selectedIds.value, id]
  }
}

function toggleSelectAll(event: Event) {
  const checked = (event.target as HTMLInputElement).checked
  selectedIds.value = checked ? rows.value.map((row) => row.id) : []
}

function rememberUndo(ids: string[], message: string) {
  lastRemoved = ids
  undoMessage.value = message
  window.clearTimeout(undoTimer)
  undoTimer = setTimeout(() => {
    undoMessage.value = ''
    lastRemoved = []
  }, 5000)
}

function removeOne(id: string) {
  removeWish(id)
  selectedIds.value = selectedIds.value.filter((item) => item !== id)
  rememberUndo([id], '찜을 해제했습니다.')
}

function removeSelected() {
  if (!selectedIds.value.length) return
  const ids = [...selectedIds.value]
  removeMany(ids)
  selectedIds.value = []
  rememberUndo(ids, `${ids.length}개 상품의 찜을 해제했습니다.`)
}

function removeIssues() {
  const ids = rows.value.filter((row) => row.issue).map((row) => row.id)
  if (!ids.length) return
  removeMany(ids)
  selectedIds.value = selectedIds.value.filter((id) => !ids.includes(id))
  rememberUndo(ids, `품절상품 ${ids.length}개를 삭제했습니다.`)
}

function undoWish() {
  if (!lastRemoved.length) return
  addMany(lastRemoved)
  undoMessage.value = ''
  lastRemoved = []
  window.clearTimeout(undoTimer)
}

async function addSelectedToCart() {
  const ids = selectedIds.value.filter((id) => selectableIds.value.includes(id))
  if (!ids.length || adding.value) return
  adding.value = true
  try {
    for (const id of ids) {
      await addItem(id, 1)
    }
    selectedIds.value = []
    showSuccess(`${ids.length}개 상품을 장바구니에 담았습니다.`)
  } catch (error) {
    showError(error)
  } finally {
    adding.value = false
  }
}

watch(wishlist, () => {
  const alive = new Set(wishlist.value)
  selectedIds.value = selectedIds.value.filter((id) => alive.has(id))
})

onMounted(() => {
  void ensureLiveProducts()
})

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["mall-wish", { 'is-embedded': _unref(isEmbedded) }])
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("header", _hoisted_2, [
        _createElementVNode("div", null, [
          _cache[0] || (_cache[0] = _createElementVNode("h1", null, "찜하기", -1)),
          _createElementVNode("p", null, _toDisplayString(_unref(wishlist).length) + "개", 1)
        ]),
        (_unref(wishlist).length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createElementVNode("label", _hoisted_4, [
                _createElementVNode("input", {
                  type: "checkbox",
                  checked: allSelectableChecked.value,
                  ".indeterminate": partialSelected.value,
                  onChange: toggleSelectAll
                }, null, 40, _hoisted_5),
                _cache[1] || (_cache[1] = _createTextVNode(" 전체선택 ", -1))
              ]),
              _createElementVNode("button", {
                type: "button",
                class: "mall-wish__cart-btn",
                disabled: !selectedIds.value.length || adding.value,
                onClick: addSelectedToCart
              }, [
                _createVNode(AppIcon, {
                  icon: "mdi-cart-outline",
                  size: 18
                }),
                _createTextVNode(" " + _toDisplayString(adding.value ? '담는 중…' : `선택담기 (${selectedIds.value.length})`), 1)
              ], 8, _hoisted_6),
              _createElementVNode("button", {
                type: "button",
                class: "mall-wish__ghost-btn",
                disabled: !selectedIds.value.length,
                onClick: removeSelected
              }, " 선택삭제 ", 8, _hoisted_7),
              (issueCount.value)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    type: "button",
                    class: "mall-wish__ghost-btn",
                    onClick: removeIssues
                  }, " 품절상품삭제 "))
                : _createCommentVNode("", true)
            ]))
          : _createCommentVNode("", true)
      ]),
      (issueCount.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_8, [
            _createVNode(AppIcon, {
              icon: "mdi-alert-outline",
              size: 16
            }),
            _createTextVNode(" 품절·판매종료 상품이 " + _toDisplayString(issueCount.value) + "개 있습니다. 판매중인 상품만 장바구니에 담을 수 있습니다. ", 1)
          ]))
        : _createCommentVNode("", true),
      (!_unref(wishlist).length)
        ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
            _createVNode(AppIcon, {
              icon: "mdi-heart-off-outline",
              size: 56
            }),
            _cache[3] || (_cache[3] = _createElementVNode("p", null, "찜한 상품이 없습니다.", -1)),
            _cache[4] || (_cache[4] = _createElementVNode("em", null, "상품의 찜하기 버튼을 누르면 여기에 저장됩니다.", -1)),
            _createVNode(_component_router_link, { to: "/mall" }, {
              default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                _createTextVNode("쇼핑계속하기", -1)
              ]))]),
              _: 1
            })
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_10, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(rows.value, (row) => {
              return (_openBlock(), _createElementBlock("article", {
                key: row.id,
                class: _normalizeClass(["mall-wish__card", { 'is-issue': row.issue, 'is-selected': selectedIds.value.includes(row.id) }])
              }, [
                _createElementVNode("label", _hoisted_11, [
                  _createElementVNode("input", {
                    type: "checkbox",
                    checked: selectedIds.value.includes(row.id),
                    "aria-label": `${row.product?.name || row.id} 선택`,
                    onChange: ($event: any) => (toggleSelect(row.id))
                  }, null, 40, _hoisted_12)
                ]),
                (row.product)
                  ? (_openBlock(), _createBlock(ProductCard, {
                      key: 0,
                      product: row.product
                    }, null, 8, ["product"]))
                  : (_openBlock(), _createElementBlock("div", _hoisted_13, [
                      _createVNode(AppIcon, {
                        icon: "mdi-package-variant-remove",
                        size: 28
                      }),
                      _cache[5] || (_cache[5] = _createElementVNode("p", null, "판매가 종료된 상품입니다.", -1)),
                      _createElementVNode("button", {
                        type: "button",
                        onClick: ($event: any) => (removeOne(row.id))
                      }, "삭제", 8, _hoisted_14)
                    ])),
                (row.product?.soldOut)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_15, "품절"))
                  : _createCommentVNode("", true)
              ], 2))
            }), 128))
          ]))
    ]),
    (undoMessage.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
          _createElementVNode("span", null, _toDisplayString(undoMessage.value), 1),
          _createElementVNode("button", {
            type: "button",
            onClick: undoWish
          }, "실행 취소")
        ]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})